import { DISC_EMOJI, discName } from '../../constants/disc';
import { L } from '../CoverQuiz/quizLang';

const nameOf = (letter: 'D' | 'I' | 'S' | 'C') => L(discName(letter), discName(letter, 'zh'), discName(letter, 'en'));

/**
 * DISC archetypes for 招募測驗 — names and emoji come from constants/disc; every
 * line is in the three quiz languages (CoverQuiz/quizLang).
 *
 * Kept in its own dependency-free module, split out of CareerQuiz.tsx: the page
 * component pulls in the ClientQuiz API (and, through it, the real fetch/auth
 * chain) at module scope, and RecruitFun.tsx only ever needed this lookup table,
 * not the whole quiz page.
 */
export const DISC_ARCHETYPES = {
  D: {
    letter: 'D',
    name: nameOf('D'),
    emoji: DISC_EMOJI.D,
    color: '#EA8462',
    bg: '#fdf2ee',
    tagline: L('果敢魄力 · 開疆闢土 · 目標必達', '果敢魄力 · 开疆辟土 · 目标必达', 'Bold · Trailblazing · Goal-driven'),
    traits: [
      L(
        '天生具備領袖氣質，決策果斷且目標清晰',
        '天生具备领袖气质，决策果断且目标清晰',
        'A natural leader: decisive, with clear goals'
      ),
      L(
        '喜歡接受挑戰，重視效率與結果',
        '喜欢接受挑战，重视效率与结果',
        'Loves a challenge, values efficiency and results'
      ),
      L(
        '在自主創業與高回報賽道中爆發力極強',
        '在自主创业与高回报赛道中爆发力极强',
        'Thrives when running their own business on a high-reward track'
      )
    ],
    letterLine: L(
      '鷹型的人不缺野心，只缺一條讓你全速前進的賽道。',
      '鹰型的人不缺野心，只缺一条让你全速前进的赛道。',
      "Eagles don't lack ambition, just a track that lets you go full speed."
    )
  },
  I: {
    letter: 'I',
    name: nameOf('I'),
    emoji: DISC_EMOJI.I,
    color: '#EAC862',
    bg: '#fdfbf0',
    tagline: L(
      '魅力溝通 · 熱情感染 · 人脈磁鐵',
      '魅力沟通 · 热情感染 · 人脉磁铁',
      'Charming · Infectious energy · People magnet'
    ),
    traits: [
      L(
        '天生擅長人際交往與情感連結，極具親和力',
        '天生擅长人际交往与情感连结，极具亲和力',
        'A natural at connecting with people, warm and approachable'
      ),
      L(
        '樂於分享與啟發他人，容易贏得信任',
        '乐于分享与启发他人，容易赢得信任',
        'Loves to share and inspire, and wins trust easily'
      ),
      L(
        '非常適合團隊合作與諮詢顧問型事業',
        '非常适合团队合作与咨询顾问型事业',
        'Made for teamwork and advisory careers'
      )
    ],
    letterLine: L(
      '你的感染力就是生產力 — 值得一個讓更多人看見你的舞台。',
      '你的感染力就是生产力 — 值得一个让更多人看见你的舞台。',
      'Your energy is productivity — it deserves a stage where more people can see you.'
    )
  },
  S: {
    letter: 'S',
    name: nameOf('S'),
    emoji: DISC_EMOJI.S,
    color: '#62EA84',
    bg: '#f1fcf4',
    tagline: L(
      '誠懇可靠 · 耐心傾聽 · 長遠相伴',
      '诚恳可靠 · 耐心倾听 · 长远相伴',
      'Sincere · Patient listener · In it for the long run'
    ),
    traits: [
      L(
        '做事細心穩健，同理心極強，重視人與人的長久關係',
        '做事细心稳健，同理心极强，重视人与人的长久关系',
        'Careful and steady, deeply empathetic, values lasting relationships'
      ),
      L(
        '是客戶與團隊最信賴的安心後盾',
        '是客户与团队最信赖的安心后盾',
        'The dependable support clients and teammates trust most'
      ),
      L(
        '擅長深耕客戶關係，長期客戶留存率極高',
        '擅长深耕客户关系，长期客户留存率极高',
        'Builds deep client relationships and keeps clients for years'
      )
    ],
    letterLine: L(
      '你的可靠是最稀缺的資產，值得一份長線累積的事業。',
      '你的可靠是最稀缺的资产，值得一份长线累积的事业。',
      'Your reliability is the rarest asset, and it deserves a career that builds over the long run.'
    )
  },
  C: {
    letter: 'C',
    name: nameOf('C'),
    emoji: DISC_EMOJI.C,
    color: '#62A6EA',
    bg: '#eff6fd',
    tagline: L(
      '嚴謹邏輯 · 專業智囊 · 追求卓越',
      '严谨逻辑 · 专业智囊 · 追求卓越',
      'Rigorous · Expert adviser · Strives for excellence'
    ),
    traits: [
      L(
        '重視事實、數據與細節，邏輯思維嚴密清晰',
        '重视事实、数据与细节，逻辑思维严密清晰',
        'Values facts, data and detail, with clear, rigorous logic'
      ),
      L(
        '追求高專業度與合規標準，做事一絲不苟',
        '追求高专业度与合规标准，做事一丝不苟',
        'Holds high professional and compliance standards, meticulous in everything'
      ),
      L(
        '在理財架構與資產配置中能給出最權威的分析',
        '在理财架构与资产配置中能给出最权威的分析',
        'Gives the most authoritative analysis on financial structures and asset allocation'
      )
    ],
    letterLine: L(
      '你的專業與嚴謹，在憑實力的行業裡會被真正看見。',
      '你的专业与严谨，在凭实力的行业里会被真正看见。',
      'Your expertise and rigour will truly be seen in an industry that rewards ability.'
    )
  }
} as const;
